/**
 * components/Bar/LegendRow
 * The `auto 1fr auto` legend line under a stacked bar: the dot, then the label
 * with its count STACKED beneath it, then the amount right-aligned. The
 * three-track layout is what keeps the amounts in a column while the labels
 * vary in length.
 */
import { StyleSheet, Text, View } from 'react-native';
import { neutral } from '../../theme/colors';
import { sans, size as fontSize } from '../../theme/typography';
import Dot from '../Dot';
import Money from '../Money';

export interface LegendRowProps {
  color: string;
  label: string;
  /** "4 assignments" — the count, already pluralised by lib/format `countLabel`. */
  meta?: string;
  /** Minor units. Omit for a count-only legend (the count-results bar). */
  value?: string;
  cur?: string;
  /** Shown instead of an amount when the legend is counting, not totalling. */
  count?: number;
}

export function LegendRow({ color, label, meta, value, cur, count }: LegendRowProps) {
  return (
    <View style={styles.row}>
      <Dot color={color} />
      {/*
        The design stacks these: the scope on one line at 12.5px, its count
        beneath at 11px in grey — not joined inline with a middle dot. The
        `auto 1fr auto` grid is what keeps the amounts in a column while these
        two lines vary in width.
      */}
      <View style={styles.text}>
        <Text numberOfLines={1} style={styles.label}>
          {label}
        </Text>
        {!!meta && (
          <Text numberOfLines={1} style={styles.meta}>
            {meta}
          </Text>
        )}
      </View>
      {value !== undefined && cur !== undefined ? (
        <Money value={value} cur={cur} size={fontSize.label} align="right" />
      ) : count !== undefined ? (
        <Text style={styles.count}>{count}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  // 9px gap and `auto 1fr auto` tracks, per the design.
  row: { flexDirection: 'row', alignItems: 'center', gap: 9 },
  // flex + minWidth 0 is the `1fr` track: it takes the slack and ellipsises
  // rather than pushing the amount out of the card.
  text: { flex: 1, minWidth: 0 },
  label: {
    ...sans('400'),
    fontSize: fontSize.button,
    color: neutral.ink,
    textTransform: 'capitalize',
  },
  meta: { ...sans('400'), fontSize: fontSize.metaSm, color: neutral.grey400 },
  count: { ...sans('400'), fontSize: fontSize.button, color: neutral.ink },
});

export default LegendRow;
